import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from '@/components/ui/hover-card'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

interface MetricCardProps {
	title: string
	value: string | number
	description?: string
	icon?: React.ReactNode // Accept any ReactNode for flexibility
	trend?: {
		value: number
		label: string
	}
	className?: string
	valueClassName?: string
}

const MetricCard = ({
	title,
	value,
	description,
	icon,
	trend,
	className,
	valueClassName,
}: MetricCardProps) => {
	const { t } = useTranslation()
	const isPositive = trend && trend.value > 0
	const isNegative = trend && trend.value < 0
	const trendValue = trend ? trend.value.toFixed(1) : null

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}>
			<HoverCard>
				<HoverCardTrigger asChild>
					<Card
						className={cn(
							'group transition-all duration-300 hover:shadow-md hover:border-primary/20 cursor-pointer overflow-hidden bg-card', // Use bg-card
							className
						)}>
						<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
							<CardTitle className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">
								{title}
							</CardTitle>
							{icon && (
								<div className="text-muted-foreground group-hover:text-primary transition-colors">
									{icon}
								</div>
							)}
						</CardHeader>
						<CardContent>
							<div className="flex items-baseline gap-2">
								<div
									className={cn(
										'text-2xl font-bold tracking-tight',
										valueClassName
									)}>
									{value}
								</div>
								{trend && trendValue !== null && (
									<span
										className={cn(
											'text-xs font-medium flex items-center gap-1', // Use text-xs and flex
											isPositive && 'text-green-600 dark:text-green-400', // Use specific green
											isNegative && 'text-red-600 dark:text-red-400', // Use specific red
											!isPositive && !isNegative && 'text-muted-foreground' // Style for zero trend
										)}>
										{isPositive && (
											<TrendingUp className="h-3 w-3" strokeWidth={2.5} />
										)}
										{isNegative && (
											<TrendingDown className="h-3 w-3" strokeWidth={2.5} />
										)}
										{trendValue}%
									</span>
								)}
							</div>
							{description && (
								<p className="text-xs text-muted-foreground mt-1 group-hover:text-foreground/80 transition-colors truncate">
									{description}
								</p>
							)}
						</CardContent>
					</Card>
				</HoverCardTrigger>
				{/* Keep HoverCardContent for extra details if needed */}
				<HoverCardContent className="w-auto max-w-xs">
					<div className="flex justify-between space-x-4">
						<div className="space-y-1">
							<h4 className="text-sm font-semibold">{title}</h4>
							{description && (
								<p className="text-sm text-muted-foreground">{description}</p>
							)}
							{trend && (
								<p className="text-sm">
									<span className="font-medium">{t('metricCard.trend')}: </span>
									{trend.label} ({isPositive && '+'}
									{trendValue}%)
								</p>
							)}
						</div>
					</div>
				</HoverCardContent>
			</HoverCard>
		</motion.div>
	)
}

export default MetricCard
